import React, { useState } from 'react'
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Button,
  Paper,
  Stack,
} from '@mui/material'
import type { AreaData } from '@/utilities/types/table'
import DateFormatter from '@/utilities/date-formatter'
import CloseIcon from '@mui/icons-material/Close'

interface AreaChipsDisplayProps {
  areas: AreaData[]
}

export const AreaChipsDisplay: React.FC<AreaChipsDisplayProps> = ({
  areas,
}) => {
  const [selectedArea, setSelectedArea] = useState<AreaData | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false)
  console.log(
    'Area Names:',
    areas.map((a) => a.name),
  )
  const handleChipClick = (area: AreaData) => {
    setSelectedArea(area)
    setIsModalOpen(true)
  }

  const handleViewAllClick = () => {
    setIsViewAllModalOpen(true)
  }

  const renderChips = () => {
    if (areas.length <= 1) {
      return [...areas]
        .sort((a, b) => a.area.name.localeCompare(b.area.name))
        .map((area) => (
          <Chip
            key={area.area.id}
            label={area.area.code}
            onClick={() => handleChipClick(area)}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.08)',
              },
              margin: '2px',
            }}
            variant="outlined"
            color="primary"
          />
        ))
    }

    return (
      <Chip
        label={`View All Areas (${areas.length})`}
        onClick={handleViewAllClick}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.08)',
          },
        }}
        variant="outlined"
        color="primary"
      />
    )
  }
  if (!areas || areas.length === 0) {
    return <span>-</span>
  }

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {renderChips()}
      </Stack>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedArea && (
          <>
            <DialogTitle
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {areas.length > 2 && (
                  <Button
                    onClick={() => {
                      setIsModalOpen(false)
                      setIsViewAllModalOpen(true)
                    }}
                    sx={{
                      minWidth: 'auto',
                      textTransform: 'none',
                      p: 1,
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Back
                  </Button>
                )}
                <Typography variant="h6">Area Details</Typography>
              </Box>
              <IconButton
                onClick={() => setIsModalOpen(false)}
                sx={{ width: 20, height: 20 }}
              >
                <CloseIcon
                  sx={{
                    color: '#505470',
                  }}
                />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {selectedArea.area.name}
                </Typography>
                {selectedArea.area.description && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedArea.area.description}
                  </Typography>
                )}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Area Code:</Typography>
                  <Typography>{selectedArea.area.code}</Typography>
                </Box>
                {selectedArea.parentArea && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Parent Area:</Typography>
                    <Typography>{selectedArea.area.parentArea}</Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  {selectedArea.area.branches.map((branch) => (
                    <Chip
                      key={branch.id}
                      label={`${branch.dealerName} - ${branch.name}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Created:{' '}
                    <DateFormatter dateString={selectedArea.dateCreated} />
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Modified:{' '}
                    <DateFormatter dateString={selectedArea.dateModified} />
                  </Typography>
                </Box>
              </Paper>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={isViewAllModalOpen}
        onClose={() => setIsViewAllModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">All Areas</Typography>
          <IconButton
            onClick={() => setIsViewAllModalOpen(false)}
            sx={{ width: 20, height: 20 }}
          >
            <CloseIcon
              sx={{
                color: '#505470',
              }}
            />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            {[...areas]
              .sort((a, b) => a.area.name.localeCompare(b.area.name))
              .map((area) => (
                <Paper
                  key={area.id}
                  elevation={1}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(33, 150, 243, 0.04)',
                    },
                  }}
                  onClick={() => {
                    setIsViewAllModalOpen(false)
                    handleChipClick(area)
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {area.area.name}
                  </Typography>
                  {area.area.description && (
                    <Typography variant="body2" color="text.secondary">
                      {area.description}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Code: {area.area.code}
                  </Typography>
                </Paper>
              ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
